import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Container,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  HStack,
  Button,
  Select,
  Card,
  CardBody,
  useBreakpointValue,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Image,
  SimpleGrid,
} from '@chakra-ui/react'
import { FiTrash2, FiEdit, FiPlus, FiX } from 'react-icons/fi'
import {
  getAdminAuctionCaseList,
  deleteAdminAuctionCase,
  createAuctionCase,
  updateAuctionCase,
  uploadImage,
} from '../../services/apiService'
import dayjs from 'dayjs'

const adminNavItems = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

interface AuctionCaseItem {
  id: number
  title: string
  content: string
  images: string[] | null
  viewCount: number
  createdAt: string
  updatedAt: string | null
}

const AuctionCase = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const [auctionCases, setAuctionCases] = useState<AuctionCaseItem[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const deleteDialog = useDisclosure()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingItem, setEditingItem] = useState<AuctionCaseItem | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formImages, setFormImages] = useState<File[]>([])
  const [existingImages, setExistingImages] = useState<string[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchData = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAdminAuctionCaseList(page, limit)
      if (res.statusCode === 200) {
        setAuctionCases(res.data.auctionCases)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page, limit])

  const handleDelete = async () => {
    if (!deletingId) return
    try {
      const { data: res } = await deleteAdminAuctionCase(deletingId)
      if (res.statusCode === 200) {
        toast({ title: res.message, status: 'success', duration: 3000 })
        fetchData()
      }
    } catch (error) {
      toast({ title: '삭제 실패', status: 'error', duration: 3000 })
    } finally {
      deleteDialog.onClose()
      setDeletingId(null)
    }
  }

  const handleOpenCreate = () => {
    setIsEditMode(false)
    setEditingItem(null)
    setFormTitle('')
    setFormContent('')
    setFormImages([])
    setExistingImages([])
    setImagePreviews([])
    onOpen()
  }

  const handleOpenEdit = (item: AuctionCaseItem) => {
    setIsEditMode(true)
    setEditingItem(item)
    setFormTitle(item.title)
    setFormContent(item.content)
    setFormImages([])
    setExistingImages(item.images || [])
    setImagePreviews([])
    onOpen()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      const newFiles = Array.from(files)
      setFormImages((prev) => [...prev, ...newFiles])
      newFiles.forEach((file) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, reader.result as string])
        }
        reader.readAsDataURL(file)
      })
    }
  }

  const handleRemoveNewImage = (index: number) => {
    setFormImages((prev) => prev.filter((_, i) => i !== index))
    setImagePreviews((prev) => prev.filter((_, i) => i !== index))
  }

  const handleRemoveExistingImage = (index: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formContent.trim()) {
      toast({ title: '제목과 내용을 입력해주세요.', status: 'warning', duration: 3000 })
      return
    }

    setUploading(true)
    try {
      let uploadedImageUrls: string[] = []
      for (const file of formImages) {
        const { data: uploadRes } = await uploadImage(file)
        if (uploadRes.statusCode === 200) {
          uploadedImageUrls.push(uploadRes.data.url)
        }
      }
      const allImages = [...existingImages, ...uploadedImageUrls]

      if (isEditMode && editingItem) {
        const { data: res } = await updateAuctionCase(
          editingItem.id,
          formTitle,
          formContent,
          allImages.length > 0 ? allImages : undefined
        )
        if (res.statusCode === 200) {
          toast({ title: res.message, status: 'success', duration: 3000 })
          onClose()
          fetchData()
        }
      } else {
        const { data: res } = await createAuctionCase(
          formTitle,
          formContent,
          allImages.length > 0 ? allImages : undefined
        )
        if (res.statusCode === 200) {
          toast({ title: res.message, status: 'success', duration: 3000 })
          onClose()
          fetchData()
        }
      }
    } catch (error) {
      toast({ title: '저장 실패', status: 'error', duration: 3000 })
    } finally {
      setUploading(false)
    }
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Heading as="h1" size="lg">
              경매사례 관리
            </Heading>
            <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={handleOpenCreate}>
              등록
            </Button>
          </HStack>

          <AdminNav items={adminNavItems} />

          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                {isMobile ? (
                  auctionCases.map((item) => (
                    <Card key={item.id} size="sm">
                      <CardBody>
                        <VStack align="stretch" spacing={2}>
                          <Text fontWeight="bold">{item.title}</Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={2}>
                            {item.content}
                          </Text>
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              {dayjs(item.createdAt).format('YYYY-MM-DD')}
                            </Text>
                            <HStack>
                              <IconButton
                                aria-label="수정"
                                icon={<FiEdit />}
                                size="sm"
                                onClick={() => handleOpenEdit(item)}
                              />
                              <IconButton
                                aria-label="삭제"
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => {
                                  setDeletingId(item.id)
                                  deleteDialog.onOpen()
                                }}
                              />
                            </HStack>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>ID</Th>
                          <Th>제목</Th>
                          <Th>조회수</Th>
                          <Th>등록일</Th>
                          <Th textAlign="center">관리</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {auctionCases.map((item) => (
                          <Tr key={item.id}>
                            <Td>{item.id}</Td>
                            <Td>{item.title}</Td>
                            <Td>{item.viewCount}</Td>
                            <Td>{dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}</Td>
                            <Td textAlign="center">
                              <HStack spacing={2} justify="center">
                                <IconButton
                                  aria-label="수정"
                                  icon={<FiEdit />}
                                  size="sm"
                                  onClick={() => handleOpenEdit(item)}
                                />
                                <IconButton
                                  aria-label="삭제"
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => {
                                    setDeletingId(item.id)
                                    deleteDialog.onOpen()
                                  }}
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                <HStack justify="space-between" pt={4}>
                  <HStack>
                    <Text fontSize="sm">페이지당:</Text>
                    <Select
                      size="sm"
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                      w="80px"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Select>
                  </HStack>
                  <HStack>
                    <Button size="sm" onClick={() => setPage(page - 1)} isDisabled={page <= 1}>
                      이전
                    </Button>
                    <Text fontSize="sm">
                      {page} / {totalPages || 1}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      isDisabled={page >= totalPages}
                    >
                      다음
                    </Button>
                  </HStack>
                </HStack>
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>

      {/* 등록/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditMode ? '경매사례 수정' : '경매사례 등록'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>제목</FormLabel>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="제목을 입력하세요"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>내용</FormLabel>
                <Textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder="내용을 입력하세요"
                  minH="200px"
                />
              </FormControl>

              <FormControl>
                <FormLabel>이미지</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  display="none"
                />
                <Button onClick={() => fileInputRef.current?.click()}>이미지 선택</Button>

                {existingImages.length > 0 && (
                  <Box mt={4}>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                      기존 이미지
                    </Text>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                      {existingImages.map((url, idx) => (
                        <Box key={idx} position="relative">
                          <Image src={url} alt="" borderRadius="md" objectFit="cover" h="100px" />
                          <IconButton
                            aria-label="삭제"
                            icon={<FiX />}
                            size="xs"
                            position="absolute"
                            top={1}
                            right={1}
                            onClick={() => handleRemoveExistingImage(idx)}
                          />
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}

                {imagePreviews.length > 0 && (
                  <Box mt={4}>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                      새 이미지
                    </Text>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                      {imagePreviews.map((preview, idx) => (
                        <Box key={idx} position="relative">
                          <Image
                            src={preview}
                            alt=""
                            borderRadius="md"
                            objectFit="cover"
                            h="100px"
                          />
                          <IconButton
                            aria-label="삭제"
                            icon={<FiX />}
                            size="xs"
                            position="absolute"
                            top={1}
                            right={1}
                            onClick={() => handleRemoveNewImage(idx)}
                          />
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button colorScheme="brand" onClick={handleSubmit} isLoading={uploading}>
              {isEditMode ? '수정' : '등록'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>경매사례 삭제</AlertDialogHeader>
            <AlertDialogBody>정말로 삭제하시겠습니까?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default AuctionCase
